import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ANSWER_MODES, INSERTED_IMAGE_WIDTH } from "./answerModes";

let seq = 0;
const nextId = (prefix) => `${prefix}-${Date.now()}-${(seq += 1)}`;

/**
 * Load a picture so the canvas can draw it.
 *
 * An attachment arrives as a url — an upload, or something the server
 * already has — but the canvas draws HTMLImageElements. Without this, a
 * student who uploaded a photo and opened it to annotate got an empty
 * canvas, because the picture had nothing for Konva to paint.
 *
 * crossOrigin is set so the result can still be flattened on submit; a
 * tainted canvas cannot be read back.
 */
const loadImage = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
    return undefined;
  });

/**
 * One answer in progress: what was typed, what was attached, what was drawn.
 *
 * All of it lives here rather than in the box so the box stays a view. Every
 * operation is a stable callback and the returned object is memoised, so a
 * keystroke re-renders the text area without re-rendering the canvas, the
 * attachment strip or the action bar.
 *
 * Attachments and strokes are separate lists: an attachment is a picture the
 * student put in, a stroke is ink they drew over it, and deleting a picture
 * should not erase the working written beside it.
 */
/** Where a newly loaded picture sits on the canvas, and how big. */
const placed = (img, index = 0) => {
  const scale = INSERTED_IMAGE_WIDTH / (img.naturalWidth || INSERTED_IMAGE_WIDTH);
  return {
    image: img,
    width: INSERTED_IMAGE_WIDTH,
    height: Math.round((img.naturalHeight || INSERTED_IMAGE_WIDTH) * scale),
    // Staggered, so two pictures do not land exactly on top of each other.
    x: 16 + index * 18,
    y: 16 + index * 18,
  };
};

export function useAnswerDraft({ initialText = "", initialImages = [] } = {}) {
  const [mode, setMode] = useState(ANSWER_MODES.TYPE);
  const [text, setText] = useState(initialText);
  const [attachments, setAttachments] = useState(() =>
    initialImages.map((url) => ({
      id: nextId("att"),
      kind: "url",
      url: typeof url === "string" ? url : url?.url,
      name: typeof url === "string" ? url : url?.name,
    }))
  );
  const [strokes, setStrokes] = useState([]);

  // Pictures the answer already had, loaded once so opening one to draw on
  // shows it rather than an empty canvas.
  useEffect(() => {
    let live = true;
    attachments
      .filter((att) => !att.image && att.url)
      .forEach((att) => {
        loadImage(att.url).then((img) => {
          if (!live || !img) return;
          setAttachments((prev) =>
            prev.map((a2, i) => (a2.id === att.id ? { ...a2, ...placed(img, i) } : a2))
          );
        });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments.length]);

  // Which figures are already in, so the add menu can tick them and a second
  // click does not stack two copies of the same diagram.
  const figureIds = useMemo(
    () => new Set(attachments.filter((att) => att.figureId).map((att) => att.figureId)),
    [attachments]
  );

  const addAttachment = useCallback((attachment) => {
    const { strokes: carried, ...rest } = attachment;
    const created = { id: nextId("att"), ...rest };

    // A figure arriving with an image already loaded can be placed at once.
    if (created.image && !created.width) {
      setAttachments((prev) => [...prev, { ...created, ...placed(created.image, prev.length) }]);
    } else {
      setAttachments((prev) => [...prev, created]);
    }

    /**
     * Marks made on the figure elsewhere become ordinary strokes here.
     *
     * They arrive as fractions of the figure, so they are scaled to wherever
     * it has been placed. Becoming ordinary strokes is the point: they can
     * then be drawn over, undone and cleared like anything else the student
     * draws, instead of being pixels they cannot touch.
     */
    if (carried && carried.length && created.image) {
      const box = placed(created.image, 0);
      setStrokes((prev) => [
        ...prev,
        ...carried.map((stroke) => ({
          id: nextId("stroke"),
          ...stroke,
          points: stroke.points.map((value, i) =>
            i % 2 === 0 ? box.x + value * box.width : box.y + value * box.height
          ),
        })),
      ]);
    }

    // The picture follows when it has loaded, so the canvas can draw it.
    if (!created.image && created.url) {
      loadImage(created.url).then((img) => {
        if (!img) return;
        setAttachments((prev) =>
          prev.map((att, i) => (att.id === created.id ? { ...att, ...placed(img, i) } : att))
        );
      });
    }
    return created;
  }, []);

  /**
   * Put a figure from the question into the answer.
   *
   * Dropping a picture in switches the box to the pen, because the reason to
   * bring a diagram across is to draw on it — that is the whole flow, and
   * making the student reach for the pen afterwards is the step this replaces.
   */
  const insertFigure = useCallback(
    (figure) => {
      if (figureIds.has(figure.id)) return null;
      const scale = INSERTED_IMAGE_WIDTH / (figure.width || INSERTED_IMAGE_WIDTH);
      const created = {
        id: nextId("att"),
        kind: "figure",
        figureId: figure.id,
        label: figure.label,
        image: figure.image,
        width: INSERTED_IMAGE_WIDTH,
        height: Math.round((figure.height || INSERTED_IMAGE_WIDTH) * scale),
        x: 16,
        y: 16,
      };
      setAttachments((prev) => [...prev, created]);
      setMode(ANSWER_MODES.PEN);
      return created;
    },
    [figureIds]
  );

  /** Remove whatever came in under this name — how a detached figure leaves. */
  const removeAttachmentNamed = useCallback((name) => {
    setAttachments((prev) => prev.filter((att) => att.name !== name));
  }, []);

  const removeAttachment = useCallback((id) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  }, []);

  const moveAttachment = useCallback((id, position) => {
    setAttachments((prev) =>
      prev.map((att) => (att.id === id ? { ...att, ...position } : att))
    );
  }, []);

  const addStroke = useCallback((stroke) => {
    setStrokes((prev) => [...prev, { id: nextId("stroke"), ...stroke }]);
  }, []);

  const undoStroke = useCallback(() => {
    setStrokes((prev) => prev.slice(0, -1));
  }, []);

  /** Put the draft back to a known state — what a cancelled edit needs. */
  const reset = useCallback((snapshot = {}) => {
    setText(snapshot.text || "");
    setAttachments(snapshot.attachments || []);
    setStrokes(snapshot.strokes || []);
    setMode(ANSWER_MODES.TYPE);
  }, []);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === ANSWER_MODES.TYPE ? ANSWER_MODES.PEN : ANSWER_MODES.TYPE));
  }, []);

  // What was last handed to the server, so the box can tell a real edit from
  // a re-render and not offer to submit the same answer twice.
  const submitted = useRef(null);
  const markSubmitted = useCallback((snapshot) => {
    submitted.current = snapshot;
  }, []);

  const isEmpty = !text.trim() && attachments.length === 0 && strokes.length === 0;

  return useMemo(
    () => ({
      mode, setMode, toggleMode,
      text, setText,
      attachments, addAttachment, insertFigure, removeAttachment, removeAttachmentNamed, moveAttachment,
      figureIds,
      strokes, addStroke, undoStroke, setStrokes,
      reset,
      isEmpty, submitted, markSubmitted,
    }),
    [
      mode, toggleMode, text, attachments, addAttachment, insertFigure,
      removeAttachment, removeAttachmentNamed, moveAttachment, figureIds, strokes, addStroke,
      undoStroke, reset, isEmpty, markSubmitted,
    ]
  );
}
